import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Users,
  BookOpen,
  GraduationCap,
  ShieldCheck,
  DollarSign,
  TrendingUp,
} from "lucide-react";

const PIE_COLORS = ["#1D4ED8", "#10B981", "#0A0A0A"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    })();
  }, []);

  if (!data) {
    return (
      <div className="p-10 mono text-xs uppercase tracking-widest text-muted-foreground">
        Loading analytics…
      </div>
    );
  }
  const { totals, enrolments_by_category, top_courses, recent_certificates } = data;

  const catData = Object.entries(enrolments_by_category).map(([k, v]) => ({
    name: k,
    value: v,
  }));

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Administrator · Console
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
          Platform analytics
        </h1>
      </div>

      {/* KPIs */}
      <div className="mb-10 grid grid-cols-2 gap-px bg-foreground sm:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={<Users className="h-4 w-4" />} label="Users" value={totals.users} />
        <Kpi icon={<GraduationCap className="h-4 w-4" />} label="Students" value={totals.students} />
        <Kpi icon={<BookOpen className="h-4 w-4" />} label="Courses" value={totals.courses} />
        <Kpi icon={<TrendingUp className="h-4 w-4" />} label="Enrolments" value={totals.enrolments} />
        <Kpi icon={<ShieldCheck className="h-4 w-4" />} label="Certificates" value={totals.certificates} />
        <Kpi
          icon={<DollarSign className="h-4 w-4" />}
          label="Revenue"
          value={`R${totals.revenue.toLocaleString()}`}
          accent
        />
      </div>

      {/* Charts */}
      <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 border-2 border-foreground bg-background p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /top courses by enrolment
          </div>
          <h2 className="font-display mt-1 text-xl font-extrabold tracking-tight">
            Course performance
          </h2>
          <div className="mt-5 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top_courses} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(10,10,10,0.08)" />
                <XAxis
                  dataKey="course_title"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  tickFormatter={(v) => (v?.length > 18 ? v.slice(0, 18) + "…" : v)}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    border: "2px solid #0A0A0A",
                    borderRadius: 0,
                    fontFamily: "JetBrains Mono",
                    fontSize: 11,
                  }}
                />
                <Bar dataKey="enrolments" fill="#1D4ED8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border-2 border-foreground bg-background p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /by category
          </div>
          <h2 className="font-display mt-1 text-xl font-extrabold tracking-tight">
            Enrolment mix
          </h2>
          <div className="mt-5 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={catData}
                  innerRadius={45}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {catData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    border: "2px solid #0A0A0A",
                    borderRadius: 0,
                    fontFamily: "JetBrains Mono",
                    fontSize: 11,
                  }}
                />
                <Legend
                  wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10, textTransform: "uppercase" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent certs */}
      <div>
        <div className="mb-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            /recent issuance
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">
            Recent certificate issuances
          </h2>
        </div>
        <div className="border-2 border-foreground bg-background">
          {recent_certificates.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No certificates issued yet.
            </div>
          ) : (
            recent_certificates.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-b-0"
              >
                <div className="col-span-4 font-semibold">{c.student_name}</div>
                <div className="col-span-4 text-muted-foreground">{c.course_title}</div>
                <div className="col-span-3 break-all font-mono text-[10px] text-muted-foreground">
                  {c.cert_hash.slice(0, 24)}…
                </div>
                <div className="col-span-1 text-right mono text-[10px] uppercase">
                  #{c.block_number}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, accent }) {
  return (
    <div className={`${accent ? "bg-accent text-accent-foreground" : "bg-background"} p-5`}>
      <div className="flex items-center justify-between">
        <div className={`flex h-7 w-7 items-center justify-center ${accent ? "bg-accent-foreground text-accent" : "bg-foreground text-background"}`}>
          {icon}
        </div>
      </div>
      <div className="mt-4 font-display text-2xl font-extrabold tracking-tight">{value}</div>
      <div className={`mono mt-1 text-[10px] uppercase tracking-widest ${accent ? "text-accent-foreground/80" : "text-muted-foreground"}`}>
        {label}
      </div>
    </div>
  );
}
